import { Component, Element, Event, EventEmitter, Host, Method, Prop, h } from '@stencil/core';
import { Alignment } from '../types';
import { closestElementCrossShadowBoundary, getLabelText, hasText } from '../../utils';
import { CheckableFormCompoment, HiddenFormElementSlot, connectForm, disconnectForm } from '../../utils';

/** @slot - Adds label text. */
@Component({
  shadow: true,
  styleUrl: 'radio.css',
  tag: 'abds-radio',
})
export class Radio implements CheckableFormCompoment {
  //--------------------------------------------------------------------------
  //
  // Element
  //
  //--------------------------------------------------------------------------

  @Element() host: HTMLAbdsRadioElement;

  //--------------------------------------------------------------------------
  //
  // Props
  //
  //--------------------------------------------------------------------------

  /** Sets name attribute. */
  @Prop({ reflect: true }) name!: string;

  /** Form submission value if selected. */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) value!: string;

  /** Aligns input and label. */
  @Prop({ reflect: true }) alignment: Alignment = 'start';

  /** Sets checked state. */
  // eslint-disable-next-line @stencil/strict-mutable
  @Prop({ mutable: true, reflect: true }) checked = false;

  /** Disables radio interaction. */
  @Prop({ reflect: true }) disabled = false;

  /** Sets required state. */
  @Prop({ reflect: true }) required = false;

  /** Sets tooltip text in embedded abds-label. */
  @Prop({ reflect: true }) tooltip: string;

  //--------------------------------------------------------------------------
  //
  // Private Properties
  //
  //--------------------------------------------------------------------------

  /** hidden input element */
  hiddenElement: HTMLInputElement;

  /** text to be used for label */
  private labelText: string;

  private radioGroup: HTMLAbdsRadioElement[];

  /** toggle element */
  private toggleEl: HTMLDivElement;

  //--------------------------------------------------------------------------
  //
  // Private Methods
  //
  //--------------------------------------------------------------------------

  private onClick = (): void => {
    this.radioGroup.forEach((radio) => {
      radio.checked = false;
    });
    this.checked = true;
    this.setFocus();
    this.abdsChange.emit();
  };

  private onKeyDown = (event: KeyboardEvent): void => {
    const keys = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown', ' '];
    const key = event.key;

    if (keys.indexOf(key) === -1) return;

    const isEventRepeat = event.repeat;

    event.preventDefault();

    if (key === ' ' && !isEventRepeat) {
      this.onClick();

      return;
    }

    const enabledRadioGroup = this.radioGroup.filter(({ disabled }) => !disabled);

    const getRoundRobinIndex = (index: number, total: number, next?: boolean): number => {
      index = next ? index + 1 : Math.max(index - 1, -1);

      return (index + total) % total;
    };

    const selectItem = (items: HTMLAbdsRadioElement[], selectedIndex: number): void => {
      items[selectedIndex].click();
    };

    let currentIndex = 0;

    enabledRadioGroup.some(({ checked }, index) => {
      if (checked) {
        currentIndex = index;

        return true;
      }
    });

    if ((key === 'ArrowLeft' || key === 'ArrowUp') && !isEventRepeat) {
      selectItem(enabledRadioGroup, getRoundRobinIndex(currentIndex, enabledRadioGroup.length));
    } else if ((key === 'ArrowDown' || key === 'ArrowRight') && !isEventRepeat) {
      selectItem(enabledRadioGroup, getRoundRobinIndex(currentIndex, enabledRadioGroup.length, true));
    }
  };

  //--------------------------------------------------------------------------
  //
  // Public Methods
  //
  //--------------------------------------------------------------------------

  /**
   * Sets blur on `abds-radio`
   * @internal
   */
  @Method()
  async setBlur() {
    this.toggleEl?.blur();
  }

  /**
   * Sets focus on native `input` in `abds-radio`
   * @internal
   */
  @Method()
  async setFocus(): Promise<void> {
    this.toggleEl?.focus();
  }

  //--------------------------------------------------------------------------
  //
  //  Events
  //
  //--------------------------------------------------------------------------

  /** Fired when the element's value is changed. */
  @Event() abdsChange: EventEmitter;

  //--------------------------------------------------------------------------
  //
  // Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  connectedCallback() {
    /**
     * Finds closest parent <form> element OR if there isn't one, it goes to
     * the the document root to find all radio buttons to treat as siblings in
     * this radio group. If it goes to the document root and there are other
     * radios with the same name attr within forms in the same document, they
     * would all be added to this effective radio group, which is not desired
     * or native behavior. Use unique names between groups of radio buttons.
     */
    const radioGroupDOMRoot =
      closestElementCrossShadowBoundary(this.host, 'form') || this.host.ownerDocument.documentElement;

    this.radioGroup = [
      ...radioGroupDOMRoot.querySelectorAll(`abds-radio[name=${this.name}]`),
    ] as HTMLAbdsRadioElement[];

    this.labelText = getLabelText(this.host);

    connectForm(this);
  }

  disconnectedCallback(): void {
    disconnectForm(this);
  }

  render() {
    return (
      <Host onClick={this.disabled ? null : this.onClick} onKeyDown={this.disabled ? null : this.onKeyDown}>
        <HiddenFormElementSlot component={this} type="radio" />
        <div
          aria-checked={this.checked.toString()}
          aria-label={this.labelText || null}
          class="radio-container"
          ref={(toggleEl) => {
            this.toggleEl = toggleEl;
          }}
          role="radio"
          tabIndex={this.disabled ? undefined : 0}
        ></div>
        {hasText(this.host) && (
          <abds-label required={this.required} tooltip={this.tooltip} weight="medium">
            <slot />
          </abds-label>
        )}
      </Host>
    );
  }
}
